import { Container } from "@mui/material";
import {
  SmartAutoComplete,
  SmartCheckbox,
  SmartForm,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { ArrivalZoneDetailsDto, Coordinate } from "~/api/GeneratedApi";
import useAuth from "~/utils/hooks/useAuth";
import { useOrganizationRoles } from "~/utils/hooks/useOrganizationRoles";
import { ExtraFormProps } from "./ArrivalZoneDialog";

type ArrivalZoneFormProps = {
  zoneDefaultValues: (ArrivalZoneDetailsDto & ExtraFormProps) | undefined;
  readOnly?: boolean;
  small?: boolean;
  formId: string;
  portId?: number;
  onSubmit: (data: ArrivalZoneFormInputProps) => void;
};

export type ArrivalZoneFormInputProps = {
  id: number | null;
  name: string;
  description: string;
  portName: string;
  location: Coordinate[];
  rolesToNotify: number[];
  active: boolean;
  portId: number | null;
  organizationId: number | string;
};

function arrivalZoneToForm(
  a: (ArrivalZoneDetailsDto & ExtraFormProps) | undefined,
  portId?: number,
): ArrivalZoneFormInputProps {
  return {
    name: a?.name ?? "",
    active: a?.active ?? false,
    id: a?.id ?? null,
    description: a?.description ?? "",
    location: a?.location ?? [],
    portName: a?.port?.name ?? "",
    portId: a?.port?.id ?? portId ?? null,
    rolesToNotify: a?.rolesToNotify ?? [],
    organizationId: a?.organizationId ?? "",
  };
}

export const ArrivalZoneForm = ({
  zoneDefaultValues,
  formId,
  portId,
  readOnly,
  small,
  onSubmit,
}: ArrivalZoneFormProps) => {
  const { t } = useTranslation("arrival-zones");
  const zoneForm = useSmartForm<ArrivalZoneFormInputProps>({
    defaultValues: React.useMemo(
      () => arrivalZoneToForm(zoneDefaultValues, portId),
      [portId, zoneDefaultValues],
    ),
  });

  const { user } = useAuth();

  const { roles, isLoading } = useOrganizationRoles({
    organizationId: user?.organization.id,
  });

  return (
    <Container maxWidth={false}>
      <SmartForm<ArrivalZoneFormInputProps>
        methods={zoneForm}
        onSubmit={onSubmit}
        gridProps={{ justifyContent: "center", direction: "column" }}
        formId={formId}
      >
        <SmartTextField<ArrivalZoneFormInputProps>
          name="name"
          label={t("name")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          controllerProps={{
            rules: {
              required: { value: true, message: t("form:required") },
            },
          }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
          autoFocus
        />
        <SmartTextField<ArrivalZoneFormInputProps>
          name="description"
          label={t("description")}
          gridProps={small ? { xs: 8, lg: 5 } : { xs: 8, lg: 4 }}
          fullWidth
          autoComplete="off"
          InputProps={{ readOnly }}
          disabled={readOnly}
        />
        <SmartCheckbox
          name="active"
          label={t(`active-true`)}
          disabled={readOnly}
        />
        {/* <OrganizationSmartSelect disabled={isLoading || readOnly} /> */}
        <SmartAutoComplete<ArrivalZoneFormInputProps, number, true>
          options={roles?.results.map((x) => x.id) ?? []}
          fullWidth
          disabled={isLoading || readOnly}
          name="rolesToNotify"
          multiple
          label={t("rolesToNotify")}
          getOptionLabel={(opt) =>
            roles?.results.find((x) => x.id === opt)?.roleName ??
            t("general:unknown")
          }
          controllerProps={{
            rules: {
              required: { value: true, message: t("form:required") },
            },
          }}
          //  isOptionEqualToValue={(opt) => console.log(opt)}
        />
      </SmartForm>
    </Container>
  );
};
